/**
 * API Service für Qualyz Frontend
 */

// =============================================================================
// Admin API Endpoints
// =============================================================================

export interface ImportResult {
  status: string
  import_type: string
  result: {
    status: string
    records_processed: number
    records_imported: number
    duration_seconds: number
    error_message?: string
  }
}

export interface SequentialImportResult {
  status: string
  import_type: string
  message: string
  results: {
    company: ImportResult['result']
    fundamental: ImportResult['result']
    price: ImportResult['result']
  }
  summary: {
    company_imported: number
    fundamental_imported: number
    price_imported: number
    total_duration: number
  }
}

export class AdminService {
  private static baseUrl = (import.meta as any).env?.VITE_API_BASE_URL || 'http://localhost:8000'

  /**
   * Holt den Auth-Token aus localStorage
   */
  private static getAuthToken(): string | null {
    return localStorage.getItem('auth_token')
  }

  /**
   * Löst einen manuellen Import aus
   */
  static async triggerImport(importType: 'fundamental' | 'company' | 'price' | 'all'): Promise<ImportResult | SequentialImportResult> {
    try {
      const token = this.getAuthToken()
      if (!token) {
        throw new Error('Nicht angemeldet. Bitte melden Sie sich an.')
      }

      console.log(`Triggering ${importType} import to: ${this.baseUrl}/api/admin/import/trigger/${importType}`)
      
      const response = await fetch(`${this.baseUrl}/api/admin/import/trigger/${importType}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      })

      console.log(`Response status: ${response.status}`)
      console.log(`Response headers:`, response.headers)

      if (!response.ok) {
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`
        try {
          const errorData = await response.text()
          console.log(`Error response body:`, errorData)
          if (errorData) {
            try {
              const parsedError = JSON.parse(errorData)
              errorMessage = parsedError.detail || errorMessage
            } catch {
              errorMessage = `${errorMessage} - Response: ${errorData.substring(0, 200)}`
            }
          }
        } catch (e) {
          console.log(`Could not read error response:`, e)
        }
        throw new Error(errorMessage)
      }

      const responseText = await response.text()
      console.log(`Success response body:`, responseText)
      
      if (!responseText) {
        throw new Error('Empty response from server')
      }

      try {
        return JSON.parse(responseText)
      } catch (e) {
        throw new Error(`Invalid JSON response: ${responseText.substring(0, 200)}`)
      }
    } catch (error) {
      console.error(`Failed to trigger ${importType} import:`, error)
      throw error
    }
  }

  /**
   * Fundamentale Kennzahlen importieren
   */
  static async importFundamentals(): Promise<ImportResult> {
    return this.triggerImport('fundamental') as Promise<ImportResult>
  }

  /**
   * Unternehmensdaten importieren
   */
  static async importCompanies(): Promise<ImportResult> {
    return this.triggerImport('company') as Promise<ImportResult>
  }

  /**
   * Kursdaten importieren
   */
  static async importPrices(): Promise<ImportResult> {
    return this.triggerImport('price') as Promise<ImportResult>
  }

  /**
   * Alle Daten sequenziell importieren (Company → Fundamental → Price)
   */
  static async importAll(): Promise<SequentialImportResult> {
    return this.triggerImport('all') as Promise<SequentialImportResult>
  }

  /**
   * Backfill fehlender Kennzahlen (FCF/Revenue Growth) triggern
   */
  static async backfillMetrics(): Promise<any> {
    const token = this.getAuthToken()
    if (!token) {
      throw new Error('Nicht angemeldet. Bitte melden Sie sich an.')
    }

    const response = await fetch(`${this.baseUrl}/api/admin/backfill/metrics`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
    })

    if (!response.ok) {
      const text = await response.text()
      throw new Error(text || `Backfill fehlgeschlagen (${response.status})`)
    }

    return await response.json()
  }

  /**
   * Import-Status abrufen
   */
  static async getImportStatus(): Promise<any> {
    const token = this.getAuthToken()
    if (!token) {
      throw new Error('Nicht angemeldet. Bitte melden Sie sich an.')
    }

    const response = await fetch(`${this.baseUrl}/api/admin/import/status`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    })

    if (!response.ok) {
      throw new Error(`Status-Abfrage fehlgeschlagen: ${response.status}`)
    }

    return await response.json()
  }
}

// =============================================================================
// Watchlist API
// =============================================================================

export interface WatchlistItemDTO {
  symbol: string
  name: string
  added_at?: string
}

export interface WatchlistDTO {
  id: string
  name: string
  items: WatchlistItemDTO[]
}

export class WatchlistService {
  private static baseUrl = (import.meta as any).env?.VITE_API_BASE_URL || 'http://localhost:8000'

  private static getAuthToken(): string | null {
    return localStorage.getItem('auth_token')
  }

  static async getMyWatchlist(): Promise<WatchlistDTO> {
    const token = this.getAuthToken()
    if (!token) throw new Error('Nicht angemeldet')

    const res = await fetch(`${this.baseUrl}/api/watchlist`, {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}` },
    })
    if (!res.ok) {
      const txt = await res.text().catch(() => '')
      throw new Error(txt || `Watchlist laden fehlgeschlagen (${res.status})`)
    }
    return await res.json()
  }

  static async add(symbol: string): Promise<WatchlistDTO> {
    const token = this.getAuthToken()
    if (!token) throw new Error('Nicht angemeldet')

    const res = await fetch(`${this.baseUrl}/api/watchlist/items`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ symbol }),
    })
    if (!res.ok) {
      const txt = await res.text().catch(() => '')
      throw new Error(txt || `Hinzufügen fehlgeschlagen (${res.status})`)
    }
    return await res.json()
  }

  static async remove(symbol: string): Promise<WatchlistDTO> {
    const token = this.getAuthToken()
    if (!token) throw new Error('Nicht angemeldet')

    const res = await fetch(`${this.baseUrl}/api/watchlist/items/${encodeURIComponent(symbol)}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    })
    if (!res.ok) {
      const txt = await res.text().catch(() => '')
      throw new Error(txt || `Entfernen fehlgeschlagen (${res.status})`)
    }
    return await res.json()
  }
}

// =============================================================================
// Health Check
// =============================================================================

export interface HealthStatus {
  status: string
  timestamp: string
  services: {
    database: string
    redis: string
    fmp_api: string
  }
}

export class HealthService {
  /**
   * API-Status prüfen
   */
  static async checkHealth(): Promise<HealthStatus> {
    try {
      const baseUrl = (import.meta as any).env?.VITE_API_BASE_URL || 'http://localhost:8000'
      const response = await fetch(`${baseUrl}/health`)
      if (!response.ok) {
        throw new Error(`Health check failed: ${response.status}`)
      }
      return await response.json()
    } catch (error) {
      console.error('Health check failed:', error)
      throw error
    }
  }

  /**
   * API-Info abrufen
   */
  static async getApiInfo(): Promise<any> {
    try {
      const baseUrl = (import.meta as any).env?.VITE_API_BASE_URL || 'http://localhost:8000'
      const response = await fetch(`${baseUrl}/api/info`)
      if (!response.ok) {
        throw new Error(`API info failed: ${response.status}`)
      }
      return await response.json()
    } catch (error) {
      console.error('API info failed:', error)
      throw error
    }
  }
}
